import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1727409163340-b20cfa3fa3ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxlbGVnYW50JTIwZXVyb3BlYW4lMjBob21lJTIwaW50ZXJpb3IlMjBuYXR1cmFsJTIwbGlnaHR8ZW58MHx8fHwxNzgwNjk1NjU5fDA&ixlib=rb-4.1.0&q=85";

const POLL_MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export function PaymentSuccess() {
  const { t } = useLang();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const provider = params.get("provider"); // "lemonsqueezy" | "paypal" | <stripe>
  const paypalSubscriptionId = params.get("subscription_id");
  const [status, setStatus] = useState("pending"); // pending | paid | failed | expired
  const [error, setError] = useState("");
  const attemptsRef = useRef(0);

  useEffect(() => {
    // PayPal: subscription_id arrives in the query string after approval.
    // Sync once with PayPal, then refresh user.
    if (provider === "paypal" && paypalSubscriptionId) {
      let cancelled = false;
      (async () => {
        try {
          await api.get(`/payments/paypal/sync/${paypalSubscriptionId}`);
          const updated = await refreshUser();
          if (cancelled) return;
          setStatus(updated?.subscription?.is_active ? "paid" : "paid");
        } catch (err) {
          if (!cancelled) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
            setStatus("failed");
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    // Lemon Squeezy: subscription confirmation arrives via webhook.
    // Poll /api/auth/me until subscription.is_active becomes true.
    if (provider === "lemonsqueezy") {
      let cancelled = false;
      const pollSub = async () => {
        if (cancelled) return;
        attemptsRef.current += 1;
        try {
          const updated = await refreshUser();
          if (updated?.subscription?.is_active) {
            if (!cancelled) setStatus("paid");
            return;
          }
          if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
            if (!cancelled) setStatus("paid"); // still show success; webhook may arrive shortly
            return;
          }
          setTimeout(pollSub, POLL_INTERVAL_MS);
        } catch (err) {
          if (!cancelled) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
            setStatus("failed");
          }
        }
      };
      pollSub();
      return () => {
        cancelled = true;
      };
    }

    if (!sessionId) {
      setStatus("failed");
      setError("Sem sessão.");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
          setStatus("failed");
          setError("Tempo esgotado a confirmar o pagamento. Verifique mais tarde.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(formatApiError(err.response?.data?.detail) || err.message);
        setStatus("failed");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, provider, paypalSubscriptionId, refreshUser]);

  return (
    <PaymentShell>
      {status === "pending" && (
        <Card
          testId="payment-pending"
          title={t("app.paymentPendingTitle")}
          body={t("app.paymentPendingBody")}
        >
          <p className="font-display-italic text-[var(--ink-soft)]">— a aguardar Stripe…</p>
        </Card>
      )}
      {status === "paid" && (
        <Card
          testId="payment-success"
          title={t("app.paymentSuccessTitle")}
          body={t("app.paymentSuccessBody")}
        >
          <button onClick={() => navigate("/app")} className="btn-primary" data-testid="payment-success-to-app">
            {t("app.paymentToApp")} →
          </button>
        </Card>
      )}
      {(status === "failed" || status === "expired") && (
        <Card
          testId="payment-failed"
          title={t("app.paymentCancelTitle")}
          body={error || t("app.paymentCancelBody")}
        >
          <Link to="/" className="btn-outline" data-testid="payment-failed-back">
            {t("app.paymentBack")}
          </Link>
        </Card>
      )}
    </PaymentShell>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  return (
    <PaymentShell>
      <Card
        testId="payment-cancel"
        title={t("app.paymentCancelTitle")}
        body={t("app.paymentCancelBody")}
      >
        <Link to="/" className="btn-outline" data-testid="payment-cancel-back">
          {t("app.paymentBack")}
        </Link>
      </Card>
    </PaymentShell>
  );
}

function PaymentShell({ children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[var(--bg)]">
      <div className="hidden md:block relative">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[var(--ink)]/40" />
        <div className="absolute top-8 left-10">
          <Link to="/" className="font-display text-2xl text-[var(--bg)]">Mordomo</Link>
        </div>
        <div className="absolute bottom-12 left-10 right-10">
          <p className="label-cap text-[var(--bg)]/80">— Ao seu dispor</p>
          <p className="font-display-italic text-3xl mt-4 text-[var(--bg)] leading-snug max-w-md">
            «Quando o pagamento é parte da postura, não do peso.»
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 md:px-12 py-12">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}

function Card({ title, body, children, testId }) {
  return (
    <div className="card-flat" data-testid={testId}>
      <p className="label-cap">— Mordomo</p>
      <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-4">{title}</h1>
      <p className="font-display-italic text-[var(--ink-soft)] mt-4 text-lg leading-relaxed">{body}</p>
      <div className="mt-10">{children}</div>
    </div>
  );
}
